import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import {
    Plus, Trash2, Edit2, Search, Filter,
    Stethoscope, Activity, ShieldCheck,
    Image as ImageIcon, ChevronRight,
    CheckCircle2, AlertCircle, Loader2,
    Clock, Heart, Info, ClipboardList, Upload
} from 'lucide-react';

import toast from 'react-hot-toast';

const SubServiceManagement = () => {
    const [subServices, setSubServices] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState(null);


    const [formData, setFormData] = useState({
        service: '',
        name: '',
        image: '',
        desc: '',
        symptoms: '',
        options: '',
        recovery: '',
        eligibilityModes: ['Cash'],
        types: [{ name: '', description: '' }],
        causes: [{ title: '', desc: '' }],
        diagnosis: [{ name: '', desc: '' }],
        steps: [{ step: '01', title: '', desc: '' }],
        faqs: [{ q: '', a: '' }],
        whyChooseDoxez: '',
        status: 'active'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [subServicesRes, servicesRes] = await Promise.all([
                api.get('/sub-services'),
                api.get('/services')
            ]);
            setSubServices(subServicesRes.data.data || []);
            setServices(servicesRes.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch catalog data');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (sub) => {
        setEditingId(sub._id);
        setFormData({
            service: sub.service?._id || sub.service,
            name: sub.name,
            image: sub.image,
            desc: sub.desc,
            symptoms: sub.symptoms?.join(', ') || '',
            options: sub.options?.join(', ') || '',
            recovery: sub.recovery || '',
            eligibilityModes: sub.eligibilityModes || ['Cash'],
            types: sub.types?.length ? sub.types : [{ name: '', description: '' }],
            causes: sub.causes?.length ? sub.causes : [{ title: '', desc: '' }],
            diagnosis: sub.diagnosis?.length ? sub.diagnosis : [{ name: '', desc: '' }],
            steps: sub.steps?.length ? sub.steps : [{ step: '01', title: '', desc: '' }],
            faqs: sub.faqs?.length ? sub.faqs : [{ q: '', a: '' }],
            whyChooseDoxez: sub.whyChooseDoxez?.join('\n') || '',
            status: sub.status || 'active'
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this sub-service?')) return;
        try {
            await api.delete(`/sub-services/${id}`);
            toast.success('Deleted successfully');
            fetchData();
        } catch (error) {
            toast.error('Delete failed');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                symptoms: formData.symptoms.split(',').map(s => s.trim()).filter(s => s),
                options: formData.options.split(',').map(o => o.trim()).filter(o => o),
                types: formData.types?.filter(t => t.name?.trim()) || [],
                causes: formData.causes?.filter(c => c.title?.trim()) || [],
                diagnosis: formData.diagnosis?.filter(d => d.name?.trim()) || [],
                steps: formData.steps?.filter(s => s.title?.trim()) || [],
                faqs: formData.faqs?.filter(f => f.q?.trim()) || [],
                whyChooseDoxez: formData.whyChooseDoxez ? formData.whyChooseDoxez.split('\n').map(w => w.trim()).filter(Boolean) : []
            };

            if (editingId) {
                await api.patch(`/sub-services/${editingId}`, payload);
                toast.success('Updated successfully');
            } else {
                await api.post('/sub-services', payload);
                toast.success('Created successfully');
            }
            setShowModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Action failed');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({
            service: '',
            name: '',
            image: '',
            desc: '',
            symptoms: '',
            options: '',
            recovery: '',
            eligibilityModes: ['Cash'],
            types: [{ name: '', description: '' }],
            causes: [{ title: '', desc: '' }],
            diagnosis: [{ name: '', desc: '' }],
            steps: [{ step: '01', title: '', desc: '' }],
            faqs: [{ q: '', a: '' }],
            whyChooseDoxez: '',
            status: 'active'
        });
    };

    const toggleMode = (mode) => {
        setFormData(prev => {
            const current = prev.eligibilityModes || [];
            if (current.includes(mode)) {
                return { ...prev, eligibilityModes: current.filter(m => m !== mode) };
            } else {
                return { ...prev, eligibilityModes: [...current, mode] };
            }
        });
    };

    const handleAddType = () => {
        setFormData(prev => ({
            ...prev,
            types: [...prev.types, { name: '', description: '' }]
        }));
    };

    const handleRemoveType = (index) => {
        setFormData(prev => ({
            ...prev,
            types: prev.types.filter((_, i) => i !== index)
        }));
    };

    const handleUpdateType = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            types: prev.types.map((t, i) => i === index ? { ...t, [field]: value } : t)
        }));
    };

    const handleAddCause = () => {
        setFormData(prev => ({
            ...prev,
            causes: [...(prev.causes || []), { title: '', desc: '' }]
        }));
    };

    const handleRemoveCause = (index) => {
        setFormData(prev => ({
            ...prev,
            causes: (prev.causes || []).filter((_, i) => i !== index)
        }));
    };

    const handleUpdateCause = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            causes: (prev.causes || []).map((c, i) => i === index ? { ...c, [field]: value } : c)
        }));
    };

    const handleAddDiagnosis = () => {
        setFormData(prev => ({
            ...prev,
            diagnosis: [...(prev.diagnosis || []), { name: '', desc: '' }]
        }));
    };

    const handleRemoveDiagnosis = (index) => {
        setFormData(prev => ({
            ...prev,
            diagnosis: (prev.diagnosis || []).filter((_, i) => i !== index)
        }));
    };

    const handleUpdateDiagnosis = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            diagnosis: (prev.diagnosis || []).map((d, i) => i === index ? { ...d, [field]: value } : d)
        }));
    };

    const handleAddStep = () => {
        setFormData(prev => {
            const nextNum = String((prev.steps?.length || 0) + 1).padStart(2, '0');
            return {
                ...prev,
                steps: [...(prev.steps || []), { step: nextNum, title: '', desc: '' }]
            };
        });
    };

    const handleRemoveStep = (index) => {
        setFormData(prev => ({
            ...prev,
            steps: (prev.steps || []).filter((_, i) => i !== index)
        }));
    };

    const handleUpdateStep = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            steps: (prev.steps || []).map((s, i) => i === index ? { ...s, [field]: value } : s)
        }));
    };

    const handleAddFaq = () => {
        setFormData(prev => ({
            ...prev,
            faqs: [...(prev.faqs || []), { q: '', a: '' }]
        }));
    };

    const handleRemoveFaq = (index) => {
        setFormData(prev => ({
            ...prev,
            faqs: (prev.faqs || []).filter((_, i) => i !== index)
        }));
    };

    const handleUpdateFaq = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            faqs: (prev.faqs || []).map((f, i) => i === index ? { ...f, [field]: value } : f)
        }));
    };




    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadLoadId = toast.loading(`Uploading image...`);

        try {
            const upData = new FormData();
            upData.append('documents', file);

            const response = await api.post('/upload', upData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.data && response.data.data.files) {
                const cloudAsset = response.data.data.files[0];
                if (type === 'subservice-image') {
                    setFormData(prev => ({ ...prev, image: cloudAsset.url }));
                } else if (type === 'service-image') {
                    setServiceFormData(prev => ({ ...prev, imageUrl: cloudAsset.url }));
                } else if (type === 'service-icon') {
                    setServiceFormData(prev => ({ ...prev, iconUrl: cloudAsset.url }));
                }
                toast.success('Asset securely uploaded to S3', { id: uploadLoadId });
            }
        } catch (err) {
            toast.error('Upload failed. Check file size.', { id: uploadLoadId });
        }
    };

    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingServiceId, setEditingServiceId] = useState(null);

    const [serviceFormData, setServiceFormData] = useState({
        serviceName: '',
        description: '',
        status: 'active',
        imageUrl: '',
        iconUrl: ''
    });

    const handleOpenCreateService = () => {
        setEditingServiceId(null);
        setServiceFormData({
            serviceName: '',
            description: '',
            status: 'active',
            imageUrl: '',
            iconUrl: ''
        });
        setShowServiceModal(true);
    };

    const handleEditService = (service, e) => {
        if (e) e.stopPropagation();
        setEditingServiceId(service._id);
        setServiceFormData({
            serviceName: service.serviceName || '',
            description: service.description || '',
            status: service.status || 'active',
            imageUrl: service.imageUrl || '',
            iconUrl: service.iconUrl || ''
        });
        setShowServiceModal(true);
    };

    const handleDeleteService = async (serviceId, serviceName, e) => {
        if (e) e.stopPropagation();
        if (!window.confirm(`Are you sure you want to delete parent service "${serviceName}"? All associated procedures will lose their parent category.`)) return;
        try {
            await api.delete(`/services/${serviceId}`);
            toast.success('Parent service deleted');
            if (selectedServiceId === serviceId) setSelectedServiceId(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete service');
        }
    };

    const handleServiceSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingServiceId) {
                await api.patch(`/services/${editingServiceId}`, serviceFormData);
                toast.success('Parent Service updated successfully');
            } else {
                await api.post('/services', serviceFormData);
                toast.success('Parent Service created successfully');
            }
            setShowServiceModal(false);
            setEditingServiceId(null);
            setServiceFormData({ serviceName: '', description: '', status: 'active', imageUrl: '', iconUrl: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || `Failed to ${editingServiceId ? 'update' : 'create'} Service`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="bg-white rounded-[24px] md:rounded-[32px] p-5 md:p-8 border border-gray-100 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex items-center gap-4 md:gap-5">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-50 rounded-xl md:rounded-2xl flex items-center justify-center text-blue-600 shadow-inner shrink-0">
                        <Stethoscope className="w-6 h-6 md:w-8 md:h-8" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight italic">Procedures <span className="text-blue-600">& Treatments</span></h1>
                        <p className="text-[9px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-emerald-500"></span> Service & Sub-Service
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 md:gap-4">
                    <button
                        onClick={handleOpenCreateService}
                        className="bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 px-2 py-2.5 md:px-6 md:py-4 rounded-xl md:rounded-2xl font-black text-[8px] md:text-sm uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                        <Plus size={14} className="text-blue-600 md:w-5 md:h-5" /> Parent Service
                    </button>
                    <button
                        onClick={() => { resetForm(); setShowModal(true); }}
                        className="bg-[#143E78] hover:bg-blue-900 text-white px-2 py-2.5 md:px-8 md:py-4 rounded-xl md:rounded-2xl font-black text-[8px] md:text-sm uppercase tracking-widest shadow-xl shadow-blue-900/20 transition-all active:scale-95 flex items-center justify-center gap-1.5"
                    >
                        <Plus size={14} className="md:w-5 md:h-5" /> Sub-Service
                    </button>
                </div>
            </div>

            {/* Breadcrumb / Navigation State */}
            {selectedServiceId && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <button
                        onClick={() => setSelectedServiceId(null)}
                        className="group flex items-center gap-3 bg-white border border-gray-100 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl shadow-sm hover:shadow-md transition-all active:scale-95 self-start"
                    >
                        <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
                            <ChevronRight size={16} className="rotate-180 md:w-[18px] md:h-[18px]" />
                        </div>
                        <span className="text-[9px] md:text-[10px] font-black text-slate-600 uppercase tracking-widest">Back to Categories</span>
                    </button>
                    <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
                        <div className="px-4 py-2 md:px-6 md:py-3 bg-blue-50 rounded-xl md:rounded-2xl border border-blue-100">
                            <p className="text-[9px] md:text-[10px] font-black text-blue-600 uppercase tracking-widest">
                                Viewing: {services.find(s => s._id === selectedServiceId)?.serviceName}
                            </p>
                        </div>
                        {services.find(s => s._id === selectedServiceId) && (
                            <button
                                type="button"
                                onClick={(e) => handleEditService(services.find(s => s._id === selectedServiceId), e)}
                                className="px-3.5 py-2 md:px-5 md:py-3 bg-white border border-gray-200 hover:bg-gray-50 text-slate-700 hover:text-blue-600 rounded-xl md:rounded-2xl font-black text-[9px] md:text-[10px] uppercase tracking-widest transition-all active:scale-95 flex items-center gap-1.5 shadow-sm cursor-pointer"
                                title="Edit Parent Service"
                            >
                                <Edit2 size={13} className="text-blue-600" />
                                <span>Edit Service</span>
                            </button>
                        )}
                    </div>
                </div>
            )}


            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full h-64 flex flex-col items-center justify-center gap-4 text-gray-400">
                        <Loader2 size={40} className="animate-spin text-blue-600" />
                        <p className="font-black uppercase tracking-widest text-[10px]">Synchronizing Matrix...</p>
                    </div>
                ) : !selectedServiceId ? (
                    /* Services View */
                    services.length === 0 ? (
                        <div className="col-span-full h-64 flex flex-col items-center justify-center gap-4 text-gray-400 bg-white rounded-[32px] border border-dashed border-gray-200">
                            <Stethoscope size={40} />
                            <p className="font-black uppercase tracking-widest text-[10px]">No Parent Services Found</p>
                        </div>
                    ) : services.map((service) => (
                        <div
                            key={service._id}
                            onClick={() => setSelectedServiceId(service._id)}
                            className="bg-white rounded-[40px] border border-gray-100 shadow-sm hover:shadow-2xl hover:shadow-blue-900/10 transition-all group cursor-pointer relative overflow-hidden h-[340px] flex flex-col"
                        >
                            {/* Background Image / Overlay */}
                            <div className="absolute inset-0 z-0">
                                {service.imageUrl ? (
                                    <img src={service.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" alt="" />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50"></div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                            </div>

                            <div className="relative z-10 flex flex-col h-full p-8 justify-between">
                                <div className="flex justify-between items-start">
                                    <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white border border-white/20 shadow-xl group-hover:bg-blue-600 transition-all duration-500">
                                        {service.iconUrl ? (
                                            <img src={service.iconUrl} className="w-8 h-8 object-contain" alt="" />
                                        ) : (
                                            <Stethoscope size={30} />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="bg-blue-500 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-500/20">
                                            {subServices.filter(s => (s.service?._id || s.service) === service._id).length} Procedures
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => handleEditService(service, e)}
                                            className="w-8 h-8 rounded-full bg-white/25 hover:bg-white text-white hover:text-blue-600 backdrop-blur-md border border-white/30 flex items-center justify-center transition-all shadow-md active:scale-90 z-20 cursor-pointer"
                                            title="Edit Parent Service"
                                        >
                                            <Edit2 size={13} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => handleDeleteService(service._id, service.serviceName, e)}
                                            className="w-8 h-8 rounded-full bg-white/25 hover:bg-rose-500 text-white backdrop-blur-md border border-white/30 flex items-center justify-center transition-all shadow-md active:scale-90 z-20 cursor-pointer"
                                            title="Delete Parent Service"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <h3 className="text-3xl font-black text-white leading-tight italic tracking-tight">{service.serviceName}</h3>
                                    <p className="text-sm text-gray-300 line-clamp-2 font-medium leading-relaxed">
                                        {service.description}
                                    </p>
                                    <div className="flex items-center justify-between pt-2">
                                        <div className="flex items-center gap-2 text-blue-400 font-black text-[10px] uppercase tracking-[0.2em] group-hover:text-blue-300 transition-colors">
                                            Explore Procedures <ChevronRight size={14} className="group-hover:translate-x-2 transition-transform" />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => handleEditService(service, e)}
                                            className="px-3 py-1 bg-white/20 hover:bg-white text-white hover:text-blue-700 text-[10px] font-black uppercase tracking-wider rounded-lg backdrop-blur-md border border-white/30 flex items-center gap-1.5 transition-all active:scale-95 z-20 cursor-pointer"
                                            title="Edit Parent Service"
                                        >
                                            <Edit2 size={11} />
                                            <span>Edit</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))

                ) : (
                    /* Sub-Services View for Selected Service */
                    subServices.filter(s => (s.service?._id || s.service) === selectedServiceId).length === 0 ? (
                        <div className="col-span-full h-64 flex flex-col items-center justify-center gap-4 text-gray-400 bg-white rounded-[32px] border border-dashed border-gray-200">
                            <ClipboardList size={40} />
                            <p className="font-black uppercase tracking-widest text-[10px]">No Sub-Services in this Category</p>
                        </div>
                    ) : subServices.filter(s => (s.service?._id || s.service) === selectedServiceId).map((sub) => (
                        <div key={sub._id} className="bg-white rounded-[32px] overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 transition-all group relative animate-in slide-in-from-bottom-4">
                            {/* Image Header */}
                            <div className="h-48 bg-slate-100 relative overflow-hidden">
                                {sub.image ? (
                                    <img src={sub.image} alt={sub.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                        <ImageIcon size={48} />
                                    </div>
                                )}
                                <div className={`absolute top-4 right-4 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${sub.status === 'active' ? 'bg-emerald-500/90 text-white' : 'bg-red-500/90 text-white'}`}>
                                    {sub.status}
                                </div>
                                {sub.types && sub.types.length > 0 && (
                                    <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
                                        {sub.types.map((t, idx) => (
                                            <span key={t._id || idx} className="bg-slate-900/60 backdrop-blur-md text-white px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border border-white/10">
                                                {t.name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-8 space-y-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 leading-tight">{sub.name}</h3>
                                    <p className="text-sm text-gray-500 mt-2 line-clamp-2">{sub.desc}</p>
                                </div>

                                {sub.types && sub.types.length > 0 && (
                                    <div className="space-y-3">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Procedural Focus</p>
                                        <div className="grid gap-3">
                                            {sub.types.map((t, idx) => (
                                                <div key={idx} className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100 group/type transition-all hover:bg-blue-100/50">
                                                    <p className="text-[11px] font-black text-blue-700 uppercase tracking-tight flex items-center gap-2">
                                                        <Activity size={14} /> {t.name}
                                                    </p>
                                                    {t.description && (
                                                        <p className="text-[10px] font-medium text-slate-600 mt-1 line-clamp-2 leading-relaxed italic opacity-80">
                                                            {t.description}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-50 col-span-2 space-y-3">
                                        <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Financial Eligibility & Support</p>
                                        <div className="space-y-2">
                                            {sub.eligibilityModes?.includes('Insurance') && (
                                                <div className="flex flex-col gap-0.5">
                                                    <p className="text-[11px] font-black text-slate-800 flex items-center gap-2">
                                                        <ShieldCheck size={14} className="text-emerald-500" /> Insurance Accepted
                                                    </p>
                                                    <p className="text-[9px] font-bold text-emerald-600 pl-5">Cashless Assistance Available</p>
                                                </div>
                                            )}
                                            {sub.eligibilityModes?.includes('Ayushman') && (
                                                <div className="flex flex-col gap-0.5">
                                                    <p className="text-[11px] font-black text-slate-800 flex items-center gap-2">
                                                        <ShieldCheck size={14} className="text-emerald-500" /> Ayushman Support Available*
                                                    </p>
                                                    <p className="text-[8px] font-medium text-gray-400 italic pl-5">*Depends on eligibility and hospital approval</p>
                                                </div>
                                            )}
                                            {sub.eligibilityModes?.includes('Cash') && (
                                                <p className="text-[11px] font-black text-slate-800 flex items-center gap-2">
                                                    <Activity size={14} className="text-blue-500" /> Cash Payment Accepted
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-purple-50/50 p-4 rounded-2xl border border-purple-50 col-span-2">
                                        <p className="text-[8px] font-black text-purple-400 uppercase tracking-widest mb-1">Recovery Protocol</p>
                                        <p className="text-xs font-bold text-slate-700">{sub.recovery || 'Standard Protocol'}</p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                                    <button
                                        onClick={() => handleEdit(sub)}
                                        className="p-3 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                                    >
                                        <Edit2 size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(sub._id)}
                                        className="p-3 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>


            {/* Sub-Service Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowModal(false)}></div>
                    <div className="bg-white w-full max-w-6xl rounded-[28px] md:rounded-[36px] shadow-2xl relative z-10 overflow-hidden flex flex-col h-[94vh] max-h-[94vh] animate-in zoom-in duration-200">
                        {/* Modal Header */}
                        <div className="px-6 py-5 md:px-10 md:py-8 bg-[#143E78] text-white flex justify-between items-center">
                            <div>
                                <h2 className="text-xl md:text-2xl font-black italic">{editingId ? 'Edit' : 'Create'} Sub-Service</h2>
                                <p className="text-[9px] md:text-[10px] font-black text-blue-300 uppercase tracking-[0.2em] mt-1">Institutional Matrix Update Protocol</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-all">
                                <Plus size={24} className="rotate-45" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6 md:p-10">
                            <form onSubmit={handleSubmit} id="subservice-form" className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Parent Service <span className="text-red-500">*</span></label>
                                    <select
                                        required
                                        className="w-full px-5 py-3 md:px-6 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm md:text-base"
                                        value={formData.service}
                                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                                    >
                                        <option value="">Select Service</option>
                                        {services.map(s => (
                                            <option key={s._id} value={s._id}>{s.serviceName}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Sub-Service Name <span className="text-red-500">*</span></label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g. Heart Valve Replacement"
                                        className="w-full px-5 py-3 md:px-6 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm md:text-base"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>


                                <div className="col-span-full space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Image URL <span className="text-red-500">*</span></label>
                                    <div className="relative group">
                                        <input
                                            readOnly
                                            type="url"
                                            placeholder="Upload an image..."
                                            className="w-full pl-5 pr-24 py-3 md:pl-6 md:pr-32 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold text-slate-700 text-sm md:text-base transition-all focus:border-blue-300"
                                            value={formData.image}
                                        />
                                        <label className="absolute right-1.5 top-1.5 bottom-1.5 px-4 md:right-2 md:top-2 md:bottom-2 md:px-6 rounded-xl bg-blue-600 text-white flex items-center gap-2 cursor-pointer hover:bg-blue-700 transition-all active:scale-95 z-10">
                                            <Upload className="w-4 h-4" />
                                            <span className="text-[10px] font-black uppercase tracking-widest">Upload</span>
                                            <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'subservice-image')} />
                                        </label>
                                    </div>
                                </div>


                                <div className="col-span-full space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Description <span className="text-red-500">*</span></label>
                                    <textarea
                                        required
                                        rows={3}
                                        placeholder="Briefly describe the sub-service..."
                                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 resize-none"
                                        value={formData.desc}
                                        onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Symptoms (Comma Separated)</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Chest Pain, Fatigue, Dizziness"
                                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700"
                                        value={formData.symptoms}
                                        onChange={(e) => setFormData({ ...formData, symptoms: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Treatment Options (Comma Separated)</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Surgery, Medication, Therapy"
                                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700"
                                        value={formData.options}
                                        onChange={(e) => setFormData({ ...formData, options: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Recovery Period</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. 2-4 Weeks"
                                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700"
                                        value={formData.recovery}
                                        onChange={(e) => setFormData({ ...formData, recovery: e.target.value })}
                                    />
                                </div>

                                <div className="col-span-full space-y-3">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Eligibility Modes (Select All That Apply)</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                                        {['Cash', 'Insurance', 'Ayushman'].map(mode => (
                                            <button
                                                key={mode}
                                                type="button"
                                                onClick={() => toggleMode(mode)}
                                                className={`flex items-center justify-center gap-3 p-4 rounded-2xl border transition-all ${formData.eligibilityModes?.includes(mode) ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm' : 'bg-white border-gray-100 text-gray-400 hover:border-gray-200'}`}
                                            >
                                                <div className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all ${formData.eligibilityModes?.includes(mode) ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-200'}`}>
                                                    {formData.eligibilityModes?.includes(mode) && <CheckCircle2 size={14} />}
                                                </div>
                                                <span className="text-xs font-black uppercase tracking-widest">{mode}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="col-span-full space-y-4">
                                    <div className="flex items-center justify-between pl-1">
                                        <label className="text-xs font-black text-[#143E78] uppercase tracking-widest">Service Types & Focus Details</label>
                                        <button
                                            type="button"
                                            onClick={handleAddType}
                                            className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5 hover:text-blue-700 transition-colors"
                                        >
                                            <Plus size={14} /> Add Focus Type
                                        </button>
                                    </div>
                                    <div className="space-y-4">
                                        {formData.types.map((type, index) => (
                                            <div key={index} className="p-6 bg-slate-50 rounded-3xl border border-slate-100 space-y-4 relative group">
                                                {formData.types.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveType(index)}
                                                        className="absolute top-4 right-4 text-slate-300 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                                <div className="grid grid-cols-1 gap-4">
                                                    <div className="space-y-1.5">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Type Name (e.g. Surgical)</p>
                                                        <input
                                                            type="text"
                                                            placeholder="Enter type name..."
                                                            className="w-full px-5 py-3 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm"
                                                            value={type.name}
                                                            onChange={(e) => handleUpdateType(index, 'name', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Focus Description</p>
                                                        <textarea
                                                            rows={2}
                                                            placeholder="What does this type cover?"
                                                            className="w-full px-5 py-3 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-xs resize-none"
                                                            value={type.description}
                                                            onChange={(e) => handleUpdateType(index, 'description', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>


                                
                                {/* Causes / Why Needed */}
                                <div className="col-span-full space-y-4">
                                    <div className="flex items-center justify-between pl-1">
                                        <label className="text-xs font-black text-[#143E78] uppercase tracking-widest">Causes & Underlying Conditions</label>
                                        <button
                                            type="button"
                                            onClick={handleAddCause}
                                            className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5 hover:text-blue-700 transition-colors"
                                        >
                                            <Plus size={14} /> Add Cause
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {(formData.causes || []).map((cause, index) => (
                                            <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 relative group">
                                                {(formData.causes || []).length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveCause(index)}
                                                        className="absolute top-3 right-3 text-slate-300 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                                <div className="grid grid-cols-1 gap-2.5">
                                                    <input
                                                        type="text"
                                                        placeholder="Condition / Cause Title (e.g. Chronic Otitis Media)"
                                                        className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm"
                                                        value={cause.title}
                                                        onChange={(e) => handleUpdateCause(index, 'title', e.target.value)}
                                                    />
                                                    <textarea
                                                        rows={2}
                                                        placeholder="Description of why this condition requires treatment..."
                                                        className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-xs resize-none"
                                                        value={cause.desc}
                                                        onChange={(e) => handleUpdateCause(index, 'desc', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Diagnosis Methods */}
                                <div className="col-span-full space-y-4">
                                    <div className="flex items-center justify-between pl-1">
                                        <label className="text-xs font-black text-[#143E78] uppercase tracking-widest">Diagnosis & Diagnostic Tests</label>
                                        <button
                                            type="button"
                                            onClick={handleAddDiagnosis}
                                            className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5 hover:text-blue-700 transition-colors"
                                        >
                                            <Plus size={14} /> Add Test
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {(formData.diagnosis || []).map((diag, index) => (
                                            <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 relative group">
                                                {(formData.diagnosis || []).length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveDiagnosis(index)}
                                                        className="absolute top-3 right-3 text-slate-300 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                                <div className="grid grid-cols-1 gap-2.5">
                                                    <input
                                                        type="text"
                                                        placeholder="Test Name (e.g. Ear Microscopy / CT Scan)"
                                                        className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm"
                                                        value={diag.name}
                                                        onChange={(e) => handleUpdateDiagnosis(index, 'name', e.target.value)}
                                                    />
                                                    <textarea
                                                        rows={2}
                                                        placeholder="What does this diagnostic test evaluate?"
                                                        className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-xs resize-none"
                                                        value={diag.desc}
                                                        onChange={(e) => handleUpdateDiagnosis(index, 'desc', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 5-Step Treatment Journey */}
                                <div className="col-span-full space-y-4">
                                    <div className="flex items-center justify-between pl-1">
                                        <label className="text-xs font-black text-[#143E78] uppercase tracking-widest">Treatment Journey (Steps)</label>
                                        <button
                                            type="button"
                                            onClick={handleAddStep}
                                            className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5 hover:text-blue-700 transition-colors"
                                        >
                                            <Plus size={14} /> Add Step
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {(formData.steps || []).map((step, index) => (
                                            <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 relative group">
                                                {(formData.steps || []).length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveStep(index)}
                                                        className="absolute top-3 right-3 text-slate-300 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                                <div className="grid grid-cols-1 gap-2.5">
                                                    <div className="flex gap-3">
                                                        <input
                                                            type="text"
                                                            placeholder="01"
                                                            className="w-16 text-center px-2 py-2.5 bg-white border border-gray-100 rounded-xl outline-none font-black text-blue-600 text-sm"
                                                            value={step.step}
                                                            onChange={(e) => handleUpdateStep(index, 'step', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Step Title (e.g. Consult, Diagnose, Plan, Treat, Recover)"
                                                            className="flex-1 px-4 py-2.5 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm"
                                                            value={step.title}
                                                            onChange={(e) => handleUpdateStep(index, 'title', e.target.value)}
                                                        />
                                                    </div>
                                                    <textarea
                                                        rows={2}
                                                        placeholder="Step description..."
                                                        className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-xs resize-none"
                                                        value={step.desc}
                                                        onChange={(e) => handleUpdateStep(index, 'desc', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* FAQs */}
                                <div className="col-span-full space-y-4">
                                    <div className="flex items-center justify-between pl-1">
                                        <label className="text-xs font-black text-[#143E78] uppercase tracking-widest">Frequently Asked Questions (FAQs)</label>
                                        <button
                                            type="button"
                                            onClick={handleAddFaq}
                                            className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5 hover:text-blue-700 transition-colors"
                                        >
                                            <Plus size={14} /> Add FAQ
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {(formData.faqs || []).map((faq, index) => (
                                            <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 relative group">
                                                {(formData.faqs || []).length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFaq(index)}
                                                        className="absolute top-3 right-3 text-slate-300 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                                <div className="grid grid-cols-1 gap-2.5">
                                                    <input
                                                        type="text"
                                                        placeholder="Question (e.g. Is surgery painful?)"
                                                        className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm"
                                                        value={faq.q}
                                                        onChange={(e) => handleUpdateFaq(index, 'q', e.target.value)}
                                                    />
                                                    <textarea
                                                        rows={2}
                                                        placeholder="Answer..."
                                                        className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-xs resize-none"
                                                        value={faq.a}
                                                        onChange={(e) => handleUpdateFaq(index, 'a', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Why Choose DOXEZ */}
                                <div className="col-span-full space-y-2">
                                    <label className="text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Why Choose DOXEZ? (One point per line)</label>
                                    <textarea
                                        rows={3}
                                        placeholder="Enter key reasons to choose DOXEZ (one per line)..."
                                        className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-xs resize-none"
                                        value={formData.whyChooseDoxez}
                                        onChange={(e) => setFormData({ ...formData, whyChooseDoxez: e.target.value })}
                                    />
                                </div>

                                <div className="col-span-full flex items-center gap-4 p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
                                    <label className="text-xs font-black text-blue-600 uppercase tracking-widest">Status:</label>
                                    <div className="flex gap-4">
                                        {['active', 'inactive'].map(s => (
                                            <button
                                                key={s}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, status: s })}
                                                className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${formData.status === s ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-white text-gray-400 border border-gray-100'}`}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-5 md:px-10 md:py-8 bg-gray-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row justify-end gap-3 md:gap-4">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                form="subservice-form"
                                type="submit"
                                disabled={submitting}
                                className="bg-[#143E78] hover:bg-blue-900 text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-900/20 transition-all active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center gap-3"
                            >
                                {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                                {editingId ? 'Update Entry' : 'Publish to Catalog'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Service Modal */}
            {showServiceModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 lg:p-8">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowServiceModal(false)}></div>
                    <div className="bg-white w-full max-w-xl rounded-3xl md:rounded-[40px] shadow-2xl relative z-10 overflow-hidden flex flex-col animate-in zoom-in duration-300 max-h-[95vh]">
                        {/* Modal Header */}
                        <div className="px-6 py-4 md:px-10 md:py-8 bg-blue-600 text-white flex justify-between items-center relative z-20">
                            <div>
                                <h2 className="text-xl md:text-2xl font-black italic">{editingServiceId ? 'Edit Parent Service' : 'Create Parent Service'}</h2>
                                <p className="text-[9px] md:text-[10px] font-black text-blue-100 uppercase tracking-[0.2em] mt-1">High-Level Category Definition</p>
                            </div>
                            <button
                                onClick={() => { setShowServiceModal(false); setEditingServiceId(null); }}
                                className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all active:scale-90 cursor-pointer"
                            >
                                <Plus size={22} className="rotate-45" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 md:p-10 space-y-4 md:space-y-6 flex-1 overflow-y-auto">
                            <form onSubmit={handleServiceSubmit} id="service-form" className="space-y-4 md:space-y-6">
                                <div className="space-y-1.5 md:space-y-2">
                                    <label className="text-[10px] md:text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Service Name</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g. Cardiology, Neurology"
                                        className="w-full px-5 py-2.5 md:px-6 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm md:text-base transition-all"
                                        value={serviceFormData.serviceName}
                                        onChange={(e) => setServiceFormData({ ...serviceFormData, serviceName: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5 md:space-y-2">
                                    <label className="text-[10px] md:text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Description</label>
                                    <textarea
                                        required
                                        rows={2}
                                        placeholder="Provide a brief overview..."
                                        className="w-full px-5 py-2.5 md:px-6 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-slate-700 text-sm md:text-base resize-none transition-all"
                                        value={serviceFormData.description}
                                        onChange={(e) => setServiceFormData({ ...serviceFormData, description: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-1.5 md:space-y-2">
                                    <label className="text-[10px] md:text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Status</label>
                                    <select
                                        className="w-full px-5 py-2.5 md:px-6 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold text-slate-700 text-sm md:text-base focus:ring-4 focus:ring-blue-100 transition-all cursor-pointer"
                                        value={serviceFormData.status}
                                        onChange={(e) => setServiceFormData({ ...serviceFormData, status: e.target.value })}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    <div className="space-y-1.5 md:space-y-2">
                                        <label className="text-[10px] md:text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Image URL</label>
                                        <div className="relative group">
                                            <input
                                                type="url"
                                                placeholder="Paste URL or upload"
                                                className="w-full pl-5 pr-20 py-2.5 md:pl-6 md:pr-24 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold text-slate-700 text-sm md:text-base focus:ring-4 focus:ring-blue-100 transition-all"
                                                value={serviceFormData.imageUrl}
                                                onChange={(e) => setServiceFormData({ ...serviceFormData, imageUrl: e.target.value })}
                                            />
                                            <label className="absolute right-1 top-1 bottom-1 px-3 md:right-2 md:top-2 md:bottom-2 md:px-4 rounded-xl bg-blue-600 text-white flex items-center gap-2 cursor-pointer hover:bg-blue-700 transition-all active:scale-95 z-10">
                                                <Upload className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                                <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'service-image')} />
                                            </label>
                                        </div>
                                        {serviceFormData.imageUrl && (
                                            <div className="mt-2 h-20 w-full rounded-xl overflow-hidden border border-gray-200 bg-slate-100">
                                                <img src={serviceFormData.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-1.5 md:space-y-2">
                                        <label className="text-[10px] md:text-xs font-black text-[#143E78] uppercase tracking-widest pl-1">Icon URL <span className="text-red-500">*</span></label>
                                        <div className="relative group">
                                            <input
                                                type="url"
                                                placeholder="Paste URL or upload"
                                                className="w-full pl-5 pr-20 py-2.5 md:pl-6 md:pr-24 md:py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold text-slate-700 text-sm md:text-base focus:ring-4 focus:ring-blue-100 transition-all"
                                                value={serviceFormData.iconUrl}
                                                onChange={(e) => setServiceFormData({ ...serviceFormData, iconUrl: e.target.value })}
                                            />
                                            <label className="absolute right-1 top-1 bottom-1 px-3 md:right-2 md:top-2 md:bottom-2 md:px-4 rounded-xl bg-blue-600 text-white flex items-center gap-2 cursor-pointer hover:bg-blue-700 transition-all active:scale-95 z-10">
                                                <Upload className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                                <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'service-icon')} />
                                            </label>
                                        </div>
                                        {serviceFormData.iconUrl && (
                                            <div className="mt-2 h-20 w-20 rounded-xl overflow-hidden border border-gray-200 bg-slate-100 p-2 flex items-center justify-center">
                                                <img src={serviceFormData.iconUrl} alt="Icon Preview" className="w-full h-full object-contain" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 md:px-10 md:py-8 bg-slate-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row justify-end gap-3 md:gap-4">
                            <button
                                onClick={() => { setShowServiceModal(false); setEditingServiceId(null); }}
                                className="px-8 py-3 md:py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-gray-400 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                form="service-form"
                                type="submit"
                                disabled={submitting}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 md:px-12 py-3.5 md:py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-200 transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 cursor-pointer"
                            >
                                {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                                {editingServiceId ? 'Update Service' : 'Create Service'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubServiceManagement;

